// PlayerCard.js
// Mirrors PlayerCardView.swift exactly.
// Left: team logo (bundled → Firestore URL → jersey number fallback).
// Center: name + city. Right: year pill + position/rating pill.

import React, { useState, useEffect } from "react";
import { bundledLogo, logoURL } from "../managers/teamLogoService";
import FitText from "./FitText";

export default function PlayerCard({
  player,
  sport,
  position,
  isSelected = false,
  showRating = false,
  onTap,
  isDisabled = false,
  showCorrectFeedback = false,
  showIncorrectFeedback = false,
}) {
  const [isPressed, setIsPressed] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const sportColor = sport.hex;

  // Cards now stay mounted across picks (keyed by slot, not player), so the
  // logo-failed flag must clear whenever a new player lands in this slot —
  // otherwise one player's missing logo would hide the next player's logo too.
  useEffect(() => {
    setLogoError(false);
  }, [player.name, sport.id]);

  // Logo resolution mirrors iOS: bundled asset → Firestore URL → jersey fallback.
  // Soccer logos are landscape flags (no background); other sports are square pills.
  const isSoccer = sport.id === "soccer";
  const bundled = bundledLogo(player.city, sport.id);
  const url = bundled ? null : logoURL(player.city, sport.id);
  const logoSrc = bundled || url;
  const showLogo = !!logoSrc && !logoError;
  const leftWidth = isSoccer ? 44 : 40;   // fixed per sport → card width never shifts

  const cardBg = showCorrectFeedback
    ? "rgba(52,199,89,0.18)"
    : showIncorrectFeedback
    ? "rgba(255,59,48,0.18)"
    : isSelected
    ? `${sportColor}26`
    : isPressed
    ? "rgba(255,255,255,0.10)"
    : "#1e2233";

  const borderColor = showCorrectFeedback
    ? "rgba(52,199,89,0.9)"
    : showIncorrectFeedback
    ? "rgba(255,59,48,0.9)"
    : isSelected
    ? `${sportColor}99`
    : isPressed
    ? "rgba(255,255,255,0.20)"
    : `${sportColor}59`;

  const borderWidth = isSelected || showCorrectFeedback || showIncorrectFeedback ? 2 : 1;

  const shadowColor = showCorrectFeedback
    ? "rgba(52,199,89,0.45)"
    : showIncorrectFeedback
    ? "rgba(255,59,48,0.45)"
    : isSelected
    ? `${sportColor}59`
    : `${sportColor}26`;

  const isHighlighted = isSelected || showCorrectFeedback || showIncorrectFeedback;

  return (
    <div
      style={{
        ...styles.card,
        background: cardBg,
        border: `${borderWidth}px solid ${borderColor}`,
        boxShadow: `0 0 ${isHighlighted ? 12 : 6}px ${shadowColor}`,
        transform: isPressed && !isSelected ? "scale(0.98)" : "scale(1)",
        opacity: isDisabled ? 0.5 : 1,
        cursor: isDisabled ? "default" : "pointer",
        transition: "all 0.15s ease",
      }}
      onClick={() => { if (!isDisabled) onTap(); }}
      onMouseDown={() => { if (!isDisabled) setIsPressed(true); }}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
    >
      {/* Left: team logo (bundled → URL → jersey number fallback) */}
      {showLogo ? (
        <div
          style={{
            ...(isSoccer ? styles.logoPillSoccer : styles.logoPill),
            width: leftWidth,
          }}
        >
          <img
            src={logoSrc}
            alt=""
            onError={() => setLogoError(true)}
            style={isSoccer ? styles.logoImgSoccer : styles.logoImg}
          />
        </div>
      ) : (
        <div style={{ ...styles.jerseyPill, width: leftWidth }}>
          <span style={styles.jerseyText}>#{player.jerseyNumber}</span>
        </div>
      )}

      {/* Center: name + city */}
      <div style={styles.centerContent}>
        <FitText text={player.name} style={styles.playerName} minScale={0.4} />
        <span style={styles.playerCity}>{player.city.toUpperCase()}</span>
      </div>

      {/* Right: year + position/rating pills */}
      <div style={styles.rightPills}>
        <div style={styles.yearPill}>
          <span style={styles.yearText}>{player.year}</span>
        </div>
        {showRating ? (
          <div style={styles.ratingPill}>
            <span style={styles.ratingText}>{player.overallRating}</span>
          </div>
        ) : (
          <div style={{ ...styles.positionPill, background: sportColor }}>
            <span style={styles.positionText}>{position}</span>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  card: {
    display: "flex",
    alignItems: "center",
    padding: "0 12px",
    height: "58px",
    borderRadius: "12px",
    gap: "10px",
    userSelect: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  jerseyPill: {
    width: "40px",
    flexShrink: 0,
    padding: "6px 4px",
    background: "rgba(255,255,255,0.08)",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  jerseyText: {
    fontSize: "12px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.75)",
  },
  // Non-soccer: square logo in a light pill (matches iOS Color(white:0.75) box).
  logoPill: {
    height: "40px",
    flexShrink: 0,
    background: "#c2c2c2",
    borderRadius: "8px",
    border: "1px solid #8c8c8c",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    padding: "2px",
    boxSizing: "border-box",
  },
  logoImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
    display: "block",
  },
  // Soccer: landscape flag, no background (matches iOS 42×28 flag treatment).
  logoPillSoccer: {
    height: "28px",
    flexShrink: 0,
    borderRadius: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  logoImgSoccer: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  centerContent: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flex: 1,
    minWidth: 0,
  },
  playerName: {
    fontSize: "17px",
    fontWeight: "700",
    color: "#ffffff",
    letterSpacing: "0.3px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  playerCity: {
    fontSize: "11px",
    fontWeight: "500",
    fontStyle: "italic",
    color: "rgba(255,255,255,0.4)",
    letterSpacing: "0.8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rightPills: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },
  yearPill: {
    width: "40px",
    flexShrink: 0,
    background: "rgba(255,255,255,0.08)",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 2px",
  },
  yearText: {
    fontSize: "11px",
    fontWeight: "600",
    color: "rgba(255,255,255,0.55)",
  },
  positionPill: {
    width: "40px",
    flexShrink: 0,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 2px",
  },
  positionText: {
    fontSize: "10px",
    fontWeight: "800",
    color: "#000000",
    letterSpacing: "0.2px",
  },
  ratingPill: {
    width: "40px",
    flexShrink: 0,
    borderRadius: "8px",
    // App theme/brand color (matches iOS appTheme rgb(0.85, 1.0, 0.2)).
    background: "#D9FF33",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "8px 2px",
  },
  ratingText: {
    fontSize: "12px",
    fontWeight: "800",
    color: "#000000",
  },
};
